import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getVoiceOptions,
  getTimedVoiceover,
  generateTimedVoiceover,
  getTimedVoiceoverAudioUrl,
  VoiceOption,
  VoiceoverInfo,
} from "../services/scriptService";
import { ScriptResponse } from "../types";

const SMALL_BTN =
  "px-3 py-1 rounded-md text-xs transition whitespace-nowrap disabled:opacity-50";

// TIMED VOICEOVER: one MP3 where each shot's narration lasts exactly as long as its clip.
// Shown inside the shot list, only once shots exist.
const TimedVoiceoverPanel = ({ script }: { script: ScriptResponse }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canUse = user?.plan === "AGENCY" || (user as any)?.role === "ADMIN";
  const content = script.scriptContent || "";
  const isStory =
    script.scriptFormat === "AI_FILM" ||
    script.scriptFormat === "SHORTS" ||
    content.includes("INT.") ||
    content.includes("EXT.");

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [timed, setTimed] = useState<VoiceoverInfo | null>(null);
  const [timedUrl, setTimedUrl] = useState<string | null>(null);

  const [mode, setMode] = useState<"DESCRIBE" | "CHOOSE">("DESCRIBE");
  const [description, setDescription] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [voiceSearch, setVoiceSearch] = useState("");
  const [includeDialogue, setIncludeDialogue] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // The 8s / 10s buttons on each shot (off by default)
  const [showLengths, setShowLengths] = useState<boolean>(() => {
    try {
      return localStorage.getItem("tubescript-show-clip-lengths") === "on";
    } catch {
      return false;
    }
  });
  const toggleShowLengths = () => {
    const next = !showLengths;
    setShowLengths(next);
    try {
      localStorage.setItem("tubescript-show-clip-lengths", next ? "on" : "off");
    } catch {}
    window.dispatchEvent(new Event("clip-lengths-toggle")); // tells the shots to show or hide the buttons
  };

  // Load the voices and any saved timed voiceover the first time the section is opened
  useEffect(() => {
    if (!open || loaded || !canUse) return;
    const load = async () => {
      try {
        const [voiceList, saved] = await Promise.all([
          getVoiceOptions(),
          getTimedVoiceover(script.id).catch(() => null),
        ]);
        setVoices(voiceList);
        const nigerian = voiceList.find(
          (v) => v.voiceName === "en-NG-EzinneNeural",
        );
        setVoiceName(
          nigerian ? nigerian.voiceName : voiceList[0]?.voiceName || "",
        );
        if (saved) {
          setTimed(saved);
          setTimedUrl(await getTimedVoiceoverAudioUrl(script.id));
        }
      } catch {
        setError(
          "Could not load the voices. Please refresh the page and try again.",
        );
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [open, loaded, canUse, script.id]);

  // Free the audio from memory when leaving the page
  useEffect(() => {
    return () => {
      if (timedUrl) window.URL.revokeObjectURL(timedUrl);
    };
  }, [timedUrl]);

  // Filter voices by the search box, then group by language
  const groupedVoices = useMemo(() => {
    const q = voiceSearch.trim().toLowerCase();
    const filtered = q
      ? voices.filter(
          (v) =>
            v.label.toLowerCase().includes(q) ||
            (v.language || "").toLowerCase().includes(q),
        )
      : voices;
    const groups: Record<string, VoiceOption[]> = {};
    filtered.forEach((v) => {
      const lang = v.language || "Other";
      if (!groups[lang]) groups[lang] = [];
      groups[lang].push(v);
    });
    return groups;
  }, [voices, voiceSearch]);

  const handleGenerate = async () => {
    if (mode === "DESCRIBE" && !description.trim()) {
      setError("Describe the voice you want, or switch to Choose a voice.");
      return;
    }
    if (mode === "CHOOSE" && !voiceName) {
      setError("Choose a voice from the list.");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const readLines = isStory && includeDialogue;
      const saved =
        mode === "CHOOSE"
          ? await generateTimedVoiceover(script.id, voiceName, null, readLines)
          : await generateTimedVoiceover(
              script.id,
              null,
              description.trim(),
              readLines,
            );
      setTimed(saved);
      if (timedUrl) window.URL.revokeObjectURL(timedUrl);
      setTimedUrl(await getTimedVoiceoverAudioUrl(script.id));
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not create the timed voiceover. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!timedUrl) return;
    const link = document.createElement("a");
    link.href = timedUrl;
    link.download = `${(script.title || "voiceover").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-timed.mp3`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 mb-3">
      {/* Header: click to open or close */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <h3 className="text-gray-400 text-xs font-semibold uppercase">
            Timed voiceover{" "}
            <span className="normal-case font-normal text-gray-500">
              (optional)
            </span>
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            One MP3 that lines up with these shots: each shot's narration lasts
            exactly as long as its clip.
          </p>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {!canUse ? (
            <div className="bg-gray-900 border border-yellow-600 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-yellow-400 text-sm">
                Timed voiceovers are an Agency feature.
              </p>
              <button
                onClick={() => navigate("/pricing")}
                className={`${SMALL_BTN} bg-yellow-500 hover:bg-yellow-400 text-black font-semibold`}
              >
                Upgrade
              </button>
            </div>
          ) : !loaded ? (
            <p className="text-gray-400 text-sm">Loading voices...</p>
          ) : (
            <>
              {/* How to use it */}
              <p className="text-gray-400 text-xs">
                Place your clips one after another in your editor, in shot
                order, then drop this MP3 at the very start of the timeline.
                Each shot's narration plays under its own clip.
              </p>

              {/* Switch: 10-second clips */}
              <div className="flex items-center justify-between gap-3 bg-gray-900 border-2 border-blue-600 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold">
                    Some of my clips are 10 seconds (Gemini)
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {showLengths
                      ? "On: set each shot to 8s or 10s using the buttons on the shots below."
                      : "Off: every shot is treated as 8 seconds. Turn on if you mix Gemini (10s) and Flow (8s) clips."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleShowLengths}
                  role="switch"
                  aria-checked={showLengths}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition ${
                    showLengths ? "bg-blue-500" : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      showLengths ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Describe or choose a voice */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode("DESCRIBE")}
                  className={`${SMALL_BTN} ${mode === "DESCRIBE" ? "bg-purple-600 text-white" : "bg-gray-900 text-gray-400 hover:bg-gray-700"}`}
                >
                  Describe the voice
                </button>
                <button
                  type="button"
                  onClick={() => setMode("CHOOSE")}
                  className={`${SMALL_BTN} ${mode === "CHOOSE" ? "bg-purple-600 text-white" : "bg-gray-900 text-gray-400 hover:bg-gray-700"}`}
                >
                  Choose a voice
                </button>
              </div>

              {mode === "DESCRIBE" ? (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="For example: warm, gentle Nigerian woman, a bit slow"
                  className="w-full bg-gray-900 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm"
                />
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                    placeholder="Search voices or languages, for example: Nigeria, child"
                    className="w-full bg-gray-900 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition text-sm"
                  />
                  <select
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition text-sm"
                  >
                    {Object.keys(groupedVoices).length === 0 ? (
                      <option value="">No voices match your search</option>
                    ) : (
                      Object.entries(groupedVoices).map(([language, list]) => (
                        <optgroup key={language} label={language}>
                          {list.map((v) => (
                            <option key={v.voiceName} value={v.voiceName}>
                              {v.label}
                            </option>
                          ))}
                        </optgroup>
                      ))
                    )}
                  </select>
                </div>
              )}

              {/* Stories only: read the characters' lines, or leave pauses for them */}
              {isStory && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDialogue}
                    onChange={(e) => setIncludeDialogue(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-purple-500"
                  />
                  <span className="text-sm text-gray-300">
                    Read the characters' lines
                    <span className="block text-xs text-gray-500">
                      Leave unticked if your characters speak their own lines in
                      the video: the narrator pauses for them.
                    </span>
                  </span>
                </label>
              )}

              {error && (
                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold text-sm transition"
              >
                {generating
                  ? "Creating your timed voiceover... this can take a few minutes"
                  : timed
                    ? "Regenerate timed voiceover"
                    : "Generate timed voiceover"}
              </button>

              {timed && timedUrl && !generating && (
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-gray-300 text-sm">
                      Voice:{" "}
                      <span className="text-purple-400">
                        {timed.voiceLabel}
                      </span>
                    </p>
                    <button
                      onClick={handleDownload}
                      className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
                    >
                      Download timed MP3
                    </button>
                  </div>
                  <audio controls src={timedUrl} className="w-full" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TimedVoiceoverPanel;
